import { rnd1 } from "../../Game";
import { eKickOffResult } from "../Player/Ball";
import { eGameEndReason, Gameplay, TIMER } from "../Gameplay";
import { RoundSetup } from "./RoundSetup";
import { IFlashFxSettings } from "../HUD/FlashFx";
import { HUD } from "../HUD/HUD";
import { eTargetType } from "../Targets/TargetType";
import { TargetPropAnim } from "../Targets/Controllers/BaseController";
import { StaticTargetSettings } from "../Targets/Controllers/StaticController";
import { MovingTargetPathSectionSettings, MovingTargetSettings } from "../Targets/Controllers/MovingController";
import { eSwitchPosTargetMode, SwitchPosTargetSettings } from "../Targets/Controllers/SwitchPosController";
import { TargetSettingsBase } from "../Targets/TargetSettings";
import { FixedNumVal, NumValBase, RndNumVal, SelNumVal } from "../../Utils/NumberValue";
import { eSelectionMode, SceneSetupGroupBlockData, SceneSetupSelector } from "./SceneSetupSelector";
import { CornerTargetSettings, eCornerTargetPos } from "../Targets/Controllers/CornerController";
import { ProtectedValue } from "../../Utils/ProtectedValue";



export const enum eRoundMngState {
    /**
     * Probiha hrani kola
     */
    playRound,
    /**
     * Kolo bylo dokonceno (je znam vysledek letu mice)
     */
    roundEnded,
    /**
     * Probiha prechod do dalsiho kola
     */
    transToNextRound,
}



export class RoundManager {
    /**
     * Index kola v jakem bude dosazeno maximalni obtiznosti
     */
    private static readonly MAX_DIFFICULTY_ROUND = 30;
    /**
     * Udalost vyvolana jakmile je kolo pripraveno ke hre
     */
    static readonly EVENT_ROUND_READY = "rndMng:roundReady";

    static get instance(): RoundManager { return RoundManager._instance; }
    private static _instance: RoundManager;
    /**
     * (RO) Aktualni stav manageru
     */
    get state(): eRoundMngState { return this._state; }
    private _state: eRoundMngState;
    /**
     * Vsechna nastaveni scen rozdelene do skupin a tyto skupiny do bloku
     */
    private _sceneSetups: SceneSetupGroupBlockData[];
    /**
     * Vyber nastaveni sceny
     */
    private _sceneSetupSelector: SceneSetupSelector;
    private _sceneSetupSelectorBlockId: number;
    /**
     * Nastaveni zablesku pri poslednich vterinach
     */
    private _flashSettings: IFlashFxSettings[];
    /**
     * (RO) Index aktualniho kola
     */
    get roundId(): number { return this._roundId.value; }
    private _roundId: ProtectedValue;
    /**
     * (RO) Index pokusu na aktualni kolo
     */
    get roundAttemptId(): number { return this._roundAttemptId.value; }
    private _roundAttemptId: ProtectedValue;
    /**
     * (RO) Pomer zvyseni obtiznosti pro aktualni kolo (0 az 1)
     */
    get roundDifficultyIncRatio(): number { return Math.min(RoundManager.MAX_DIFFICULTY_ROUND, this._roundId.value) / RoundManager.MAX_DIFFICULTY_ROUND; }
    /**
     * (RO) Cas zahajeni kola
     */
    get roundStartTime(): number { return this._roundStartTime; }
    private _roundStartTime: number;
    /**
     * Cas, kdy dojde k ukonceni aktualniho kola a zahajeni prechodu do dalsiho
     * Pokud < 0, tak je casovac neaktivni
     */
    private _roundEndTime: number;
    /**
     * (RO) Nastaveni pro aktualni kolo
     */
    get roundSetup(): RoundSetup { return this._roundSetup; }
    private _roundSetup: RoundSetup;

    /**
     * Casovac pro ukonceni kola hry
     */
    //private _endTimer: TimeLoop;
    /**
     * True, pokud se ma prejit do dalsiho kola
     */
    private _startNewRound: boolean;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        RoundManager._instance = this;
        NumValBase.defaultRnd = rnd1;

        this._roundId = new ProtectedValue();
        this._roundAttemptId = new ProtectedValue();

        // Zachytavat konec hry
        Gameplay.instance.events.on(Gameplay.EVENT_GAME_END, this.handleGameEnd, this);

        const showAnims: TargetPropAnim[][] = [
            [
                // Opacity
                new TargetPropAnim(0, 1, 300, pc.CubicOut),
                // Scale
                new TargetPropAnim(1, 1, 300, pc.CubicOut)
            ],
        ];

        const hideAnims: TargetPropAnim[][] = [
            [
                // Opacity
                new TargetPropAnim(1, 0, 300, pc.CubicIn),
                // Scale
                new TargetPropAnim(1, 1, 300, pc.CubicIn)
            ],
        ];

        const rndAngle70to110 = new RndNumVal(70, 110, 5);
        const rndY0to1 = new RndNumVal(0.05, 0.95, 10);

        this._sceneSetups = [
            {
                selectionMode: eSelectionMode.inOrder,
                sceneToSelect: 2,
                groups: [
                    {
                        setups: [
                            {
                                uid: 0,
                                kickOffAngle: 90,
                                goaliePos: 0.5,
                                targets: [
                                    new StaticTargetSettings(eTargetType.round, 0, 0.95, 0.5)
                                ],
                                disableFlipSide: true,
                                tutorial: {}
                            },
                            {
                                uid: 1,
                                kickOffAngle: 70,
                                goaliePos: 0.5,
                                targets: [
                                    new StaticTargetSettings(eTargetType.round, 0, 0.1, new SelNumVal([0.1, 0.9]))
                                ],
                            },
                        ],
                        selectionMode: eSelectionMode.inOrder
                    }
                ]
            },
            {
                selectionMode: eSelectionMode.inOrder,
                sceneToSelect: 3,
                groups: [
                    {
                        setups: [
                            {
                                uid: 2,
                                kickOffAngle: 105,
                                goaliePos: 0.5,
                                targets: [
                                    new CornerTargetSettings(eCornerTargetPos.right),
                                    new MovingTargetSettings(eTargetType.round, 0, [new MovingTargetPathSectionSettings(0.05, 0.9, 0.9, 0, pc.SineInOut)], 0.75, 0.75, MovingTargetSettings.FLAG_YOYO_MOVE),
                                    new StaticTargetSettings(eTargetType.fireRing, 1, new SelNumVal([0.25, 0.3, 0.35, 0.65, 0.7, 0.75]), 0, TargetSettingsBase.FLAG_USE_PREV_TARGET_Y | TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_X)
                                ],
                                bonusTargetsProb: 100
                            },
                            {
                                uid: 3,
                                kickOffAngle: 75,
                                goaliePos: 0.5,
                                targets: [
                                    new CornerTargetSettings(eCornerTargetPos.left),
                                    new MovingTargetSettings(eTargetType.round, 0, [new MovingTargetPathSectionSettings(0.95, 0.1, -0.9, 0, pc.SineInOut)], 0.75, 0.75, MovingTargetSettings.FLAG_YOYO_MOVE),
                                    new StaticTargetSettings(eTargetType.fireRing, 1, new SelNumVal([0.25, 0.3, 0.35, 0.65, 0.7, 0.75]), 0, TargetSettingsBase.FLAG_USE_PREV_TARGET_Y | TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_X)
                                ],
                                bonusTargetsProb: 100
                            },
                            {
                                uid: 4,
                                kickOffAngle: 75,
                                goaliePos: 0.4,
                                targets: [
                                    new CornerTargetSettings(eCornerTargetPos.left),
                                    new StaticTargetSettings(eTargetType.round, 0, 0.75, new SelNumVal([0.1, 0.5, 0.9])),
                                    new MovingTargetSettings(eTargetType.fireRing, 0, [new MovingTargetPathSectionSettings(0.5, undefined, 0.5, 0, pc.SineInOut)], 0.75, 0.75, MovingTargetSettings.FLAG_YOYO_MOVE | TargetSettingsBase.FLAG_USE_PREV_TARGET_Y),
                                ],
                                bonusTargetsProb: 100
                            },
                        ],
                        selectionMode: eSelectionMode.random
                    }
                ]
            },
            {
                selectionMode: eSelectionMode.inOrder,
                sceneToSelect: 1,
                groups: [
                    {
                        setups: [
                            {
                                uid: 5,
                                kickOffAngle: 80,
                                goaliePos: 0.5,
                                targets: [
                                    new CornerTargetSettings(eCornerTargetPos.left),
                                    new SwitchPosTargetSettings(eTargetType.round, 0, [new FixedNumVal(0.05), new RndNumVal(0, 1, 10), new FixedNumVal(0.95), undefined], 3500, 3500, eSwitchPosTargetMode.inOrderFromRnd),
                                    new StaticTargetSettings(eTargetType.fireRing, 1, 0.95, 0, TargetSettingsBase.FLAG_USE_PREV_TARGET_Y | TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_X)
                                ],
                                bonusTargetsProb: 100
                            },
                        ],
                        selectionMode: eSelectionMode.random
                    },
                ]
            },
            {
                selectionMode: eSelectionMode.inOrder,
                sceneToSelect: 1,
                groups: [
                    {
                        setups: [
                            {
                                uid: 6,
                                kickOffAngle: 100,
                                kickOffDis: 17,
                                goaliePos: 0.4,
                                targets: [
                                    new CornerTargetSettings(eCornerTargetPos.left),
                                    new StaticTargetSettings(eTargetType.round, 0, 0.85, 0.25),
                                    new MovingTargetSettings(eTargetType.fireRing, 1, [new MovingTargetPathSectionSettings(0.6, 0.25, 0.4, 0, pc.SineInOut)], 0.6, 0.6, MovingTargetSettings.FLAG_YOYO_MOVE | TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_Y)
                                ],
                                bonusTargetsProb: 100,
                                defenderSettings: { sizeRatio: 0.2, pos: 0.75, distanceRatio: 0.5, minDifProbability: 100, maxDifProbability: 100 },
                                disableFlipSide: true,
                                tutorial: { bendVertex: new pc.Vec3(-2.5, 0, -38) }
                            }
                        ],
                        selectionMode: eSelectionMode.random
                    },
                ]
            },
            {
                selectionMode: eSelectionMode.random,
                groups: [
                    // Zakladni pohyb z bodu A do B a zpet
                    {
                        setups: [
                            // Terc jezdi nahoru/dolu
                            {
                                uid: 10,
                                kickOffAngle: rndAngle70to110,
                                goaliePos: 0.5,
                                targets: [
                                    new CornerTargetSettings(eCornerTargetPos.topRight),
                                    new MovingTargetSettings(eTargetType.round, 0, [new MovingTargetPathSectionSettings(new SelNumVal([0.1, 0.2, 0.3]), 0, 0, 1, pc.SineInOut)], 0.5, 1.25, MovingTargetSettings.FLAG_YOYO_MOVE),
                                    new StaticTargetSettings(eTargetType.fireRing, 1, 0, new RndNumVal(0, 1, 10), TargetSettingsBase.FLAG_USE_PREV_TARGET_X | TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_Y)
                                ],
                                enableMainTargetScaling: true,
                                defDefenderSettingsId: 0,
                            },
                            // Terc jezdi doleva/doprava
                            {
                                uid: 11,
                                kickOffAngle: rndAngle70to110,
                                goaliePos: 0.5,
                                targets: [
                                    new CornerTargetSettings(eCornerTargetPos.topRight),
                                    new MovingTargetSettings(eTargetType.round, 0, [new MovingTargetPathSectionSettings(new RndNumVal(0, 0.25, 5), rndY0to1, 0.5, 0, pc.SineInOut)], 0.5, 1.25, MovingTargetSettings.FLAG_YOYO_MOVE),
                                    new StaticTargetSettings(eTargetType.fireRing, 1, new RndNumVal(0, 0.5, 5), 0, TargetSettingsBase.FLAG_OFFSET_FROM_PREV_TARGET_X | TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_X | TargetSettingsBase.FLAG_USE_PREV_TARGET_Y)
                                ],
                                enableMainTargetScaling: true,
                                defDefenderSettingsId: 0,
                            },
                            // Terc jezdi sikmo nahoru a zpet
                            {
                                uid: 12,
                                kickOffAngle: rndAngle70to110,
                                goaliePos: 0.5,
                                targets: [
                                    new CornerTargetSettings(eCornerTargetPos.botRight),
                                    new MovingTargetSettings(eTargetType.round, 0, [new MovingTargetPathSectionSettings(new RndNumVal(0, 0.5, 5), 0, 0.5, 1, pc.SineInOut)], 0.5, 1.25, MovingTargetSettings.FLAG_YOYO_MOVE),
                                    new StaticTargetSettings(eTargetType.fireRing, 1, 0.25, 0.5, TargetSettingsBase.FLAG_OFFSET_FROM_PREV_TARGET_X | TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_X | TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_Y)
                                ],
                                enableMainTargetScaling: true,
                                defDefenderSettingsId: 0,
                            },
                            // Terci jezdi od jednoho okraje brany k druhemu a bonusovy terc se pohybuje v mensim rozmezi a jinou rychlosti
                            {
                                uid: 13,
                                groupMinSelectionId: 2,
                                kickOffAngle: rndAngle70to110,
                                goaliePos: 0.5,
                                targets: [
                                    new CornerTargetSettings(eCornerTargetPos.right),
                                    new MovingTargetSettings(eTargetType.round, 0, [new MovingTargetPathSectionSettings(0, rndY0to1, 1, 0, pc.SineInOut)], 0.75, 2, MovingTargetSettings.FLAG_YOYO_MOVE),
                                    new MovingTargetSettings(eTargetType.fireRing, 1, [new MovingTargetPathSectionSettings(new RndNumVal(0, 0.5, 5), 0, 0.5, 0, pc.SineInOut)], 0.5, 1.25, MovingTargetSettings.FLAG_YOYO_MOVE | TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_X | TargetSettingsBase.FLAG_USE_PREV_TARGET_Y),
                                ],
                                enableMainTargetScaling: true,
                                defDefenderSettingsId: 1,
                                bonusTargetsProb: 100
                            },
                            // Pohyb ze strany na stranu s bounceIn ease
                            {
                                uid: 14,
                                groupMinSelectionId: 2,
                                kickOffAngle: 100,
                                goaliePos: 0.5,
                                targets: [
                                    new CornerTargetSettings(eCornerTargetPos.topRight),
                                    new MovingTargetSettings(eTargetType.round, 0, [new MovingTargetPathSectionSettings(0.05, rndY0to1, 0.9, 0, pc.BounceIn)], 0.75, 1.5, MovingTargetSettings.FLAG_YOYO_MOVE),
                                    new StaticTargetSettings(eTargetType.fireRing, 1, 0.27, 0, TargetSettingsBase.FLAG_USE_PREV_TARGET_Y | TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_X)
                                ],
                                defDefenderSettingsId: 0,
                                bonusTargetsProb: 100
                            },
                            // Terc jezdi do obraceneho V tam a zpet (V zabira polovinu sirky brany)
                            {
                                uid: 15,
                                kickOffAngle: rndAngle70to110,
                                goaliePos: 0.6,
                                targets: [
                                    new CornerTargetSettings(eCornerTargetPos.topRight),
                                    new MovingTargetSettings(eTargetType.round, 0, [
                                        new MovingTargetPathSectionSettings(0, 0, 0.25, 1, pc.Linear),
                                        new MovingTargetPathSectionSettings(0.25, 1, 0.25, -1, pc.Linear)], 0.75, 1.25, MovingTargetSettings.FLAG_YOYO_MOVE),
                                    new MovingTargetSettings(eTargetType.fireRing, 1, [new MovingTargetPathSectionSettings(0.25, 1, 0.25, -1, pc.SineInOut)], 0.75, 1.25, MovingTargetSettings.FLAG_YOYO_MOVE | TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_X | TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_Y),
                                ],
                                enableMainTargetScaling: true,
                                defDefenderSettingsId: 1,
                            },
                            // Terc jezdi do V tam a zpet (V zabira polovinu sirky brany)
                            {
                                uid: 16,
                                kickOffAngle: rndAngle70to110,
                                goaliePos: 0.6,
                                targets: [
                                    new CornerTargetSettings(eCornerTargetPos.topRight),
                                    new MovingTargetSettings(eTargetType.round, 0, [
                                        new MovingTargetPathSectionSettings(0, 1, 0.25, -1, pc.Linear),
                                        new MovingTargetPathSectionSettings(0.25, 0, 0.25, 1, pc.Linear)], 0.75, 1.25, MovingTargetSettings.FLAG_YOYO_MOVE),
                                    new MovingTargetSettings(eTargetType.fireRing, 1, [new MovingTargetPathSectionSettings(0.25, 0, -0.25, 1, pc.SineInOut)], 0.75, 1.25, MovingTargetSettings.FLAG_YOYO_MOVE | TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_X | TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_Y),
                                ],
                                enableMainTargetScaling: true,
                                defDefenderSettingsId: 1,
                            },
                        ],
                        selectionMode: eSelectionMode.random
                    },
                    // GROUP - terce pohybujici se jen jednim smerem + preskakujici terce
                    {
                        setups: [
                            // Terc preskakuje od leveho okraje brany k pravemu (pouze 2 pozice)
                            {
                                uid: 20,
                                kickOffAngle: rndAngle70to110,
                                goaliePos: 0.5,
                                targets: [
                                    new CornerTargetSettings(eCornerTargetPos.left),
                                    new SwitchPosTargetSettings(eTargetType.round, 0, [new FixedNumVal(0.15), new RndNumVal(0, 1, 10), new FixedNumVal(0.85), undefined], 3500, 1500, eSwitchPosTargetMode.inOrderFromZero),
                                    new MovingTargetSettings(eTargetType.fireRing, 1, [new MovingTargetPathSectionSettings(0.15, 0, 0, 1, pc.SineInOut)], 0.5, 1.5, MovingTargetSettings.FLAG_YOYO_MOVE | TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_X),
                                ],
                                bonusTargetsProb: 100,
                                defDefenderSettingsId: 0
                            },
                            // Terc preskakuje od leveho okraje k pravemu (5 pozic)
                            {
                                uid: 21,
                                groupMinSelectionId: 1,
                                kickOffAngle: rndAngle70to110,
                                goaliePos: 0.5,
                                targets: [
                                    new CornerTargetSettings(eCornerTargetPos.right),
                                    new SwitchPosTargetSettings(eTargetType.round, 0, [new FixedNumVal(0.01), new RndNumVal(0, 1, 10), new FixedNumVal(0.25), undefined, new FixedNumVal(0.5), undefined, new FixedNumVal(0.75), undefined, new FixedNumVal(0.99), undefined], 3500, 1500, eSwitchPosTargetMode.inOrderFromZero),
                                    new MovingTargetSettings(eTargetType.fireRing, 1, [new MovingTargetPathSectionSettings(new SelNumVal([0.25, 0.5]), 1, 0, -1, pc.Linear)], 0.5, 1.5, MovingTargetSettings.FLAG_YOYO_MOVE | TargetSettingsBase.FLAG_OFFSET_FROM_PREV_TARGET_X | TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_X),
                                ],
                                bonusTargetsProb: 100,
                                defDefenderSettingsId: 0
                            },
                            // Od horniho okraje sikmo dolu zmizi a to same od druheho okraje
                            {
                                uid: 30,
                                groupMinSelectionId: 4,
                                kickOffAngle: rndAngle70to110,
                                goaliePos: 0.5,
                                targets: [
                                    new CornerTargetSettings(eCornerTargetPos.bot),
                                    new MovingTargetSettings(eTargetType.round, 0, [
                                        new MovingTargetPathSectionSettings(0, 1, 0.3, -1, pc.Linear, MovingTargetPathSectionSettings.FLAG_ANIM_POINT_A | MovingTargetPathSectionSettings.FLAG_ANIM_POINT_B),
                                        new MovingTargetPathSectionSettings(1, 1, -0.3, -1, pc.Linear, MovingTargetPathSectionSettings.FLAG_ANIM_POINT_A | MovingTargetPathSectionSettings.FLAG_ANIM_POINT_B),
                                    ], 0.5, 1.5, 0, showAnims[0], hideAnims[0]),
                                    new StaticTargetSettings(eTargetType.fireRing, 1, new SelNumVal([0.15, 0.85]), 0.5, TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_X | TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_Y),
                                ],
                                defDefenderSettingsId: 2,
                            },
                            // VV
                            {
                                uid: 31,
                                groupMinSelectionId: 2,
                                kickOffAngle: rndAngle70to110,
                                goaliePos: 0.5,
                                targets: [
                                    new CornerTargetSettings(eCornerTargetPos.botRight),
                                    new MovingTargetSettings(eTargetType.round, 0, [
                                        new MovingTargetPathSectionSettings(0, 1, 0.25, -1, pc.Linear, MovingTargetPathSectionSettings.FLAG_ANIM_POINT_A),
                                        new MovingTargetPathSectionSettings(0.25, 0, 0.25, 1, pc.Linear),
                                        new MovingTargetPathSectionSettings(0.5, 1, 0.25, -1, pc.Linear),
                                        new MovingTargetPathSectionSettings(0.75, 0, 0.25, 1, pc.Linear, MovingTargetPathSectionSettings.FLAG_ANIM_POINT_B),
                                    ], 0.75, 2, 0, showAnims[0], hideAnims[0]),
                                    new StaticTargetSettings(eTargetType.fireRing, 1, new SelNumVal([0.375, 0.625]), 0.5, TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_X | TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_Y),
                                ],
                                defDefenderSettingsId: 1
                            },
                            // Terc se pohybuje od okraje k druhemu, od ktereho udela bounce a pak zmizi
                            {
                                uid: 32,
                                groupMinSelectionId: 3,
                                kickOffAngle: rndAngle70to110,
                                goaliePos: 0.5,
                                targets: [
                                    new CornerTargetSettings(eCornerTargetPos.left),
                                    new MovingTargetSettings(eTargetType.round, 0, [new MovingTargetPathSectionSettings(0, rndY0to1, 1, 0, pc.BounceOut, MovingTargetPathSectionSettings.FLAG_ANIM_POINT_A | MovingTargetPathSectionSettings.FLAG_ANIM_POINT_B)], 0.75, 1.75, 0, showAnims[0], hideAnims[0]),
                                    new StaticTargetSettings(eTargetType.fireRing, 1, 0.75, 0, TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_X | TargetSettingsBase.FLAG_USE_PREV_TARGET_Y),
                                ],
                                defDefenderSettingsId: 2,
                                bonusTargetsProb: 100,
                            },
                            // Pohyb do obraceneho V od jednoho okraje brany k druhemu
                            {
                                uid: 33,
                                groupMinSelectionId: 4,
                                kickOffAngle: rndAngle70to110,
                                goaliePos: 0.5,
                                targets: [
                                    new CornerTargetSettings(eCornerTargetPos.topLeft),
                                    new MovingTargetSettings(eTargetType.round, 0, [
                                        new MovingTargetPathSectionSettings(0, 0, 0.5, 1, pc.SineIn, MovingTargetPathSectionSettings.FLAG_ANIM_POINT_A),
                                        new MovingTargetPathSectionSettings(0.5, 1, 0.5, -1, pc.SineOut, MovingTargetPathSectionSettings.FLAG_ANIM_POINT_B),
                                    ], 0.75, 2, 0, showAnims[0], hideAnims[0]),
                                    new MovingTargetSettings(eTargetType.fireRing, 1, [new MovingTargetPathSectionSettings(0.5, 1, 0.5, -1, pc.SineInOut)], 1, 2, TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_X | TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_Y | MovingTargetSettings.FLAG_YOYO_MOVE)
                                ],
                                defDefenderSettingsId: 2
                            },
                            // Terc jede nahoru u jednoho okraje brany, zmizi a objevi se u druheho okraje brany, kde jede dolu
                            {
                                uid: 34,
                                groupMinSelectionId: 4,
                                kickOffAngle: rndAngle70to110,
                                goaliePos: 0.5,
                                targets: [
                                    new CornerTargetSettings(eCornerTargetPos.topRight),
                                    new MovingTargetSettings(eTargetType.round, 0, [
                                        new MovingTargetPathSectionSettings(0.15, 0, 0, 1, pc.Linear, MovingTargetPathSectionSettings.FLAG_ANIM_POINT_A | MovingTargetPathSectionSettings.FLAG_ANIM_POINT_B),
                                        new MovingTargetPathSectionSettings(0.85, 1, 0, -1, pc.Linear, MovingTargetPathSectionSettings.FLAG_ANIM_POINT_A | MovingTargetPathSectionSettings.FLAG_ANIM_POINT_B),
                                    ], 0.3, 1, 0, showAnims[0], hideAnims[0]),
                                    new StaticTargetSettings(eTargetType.fireRing, 1, 0.85, new RndNumVal(0.25, 0.75, 10), TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_X | TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_Y),
                                ],
                                defDefenderSettingsId: 2
                            },
                            // Terc jede od horniho okraje sikmo dolu k druhemu okraji, pak zmici a udela to same z druheho okraje
                            {
                                uid: 35,
                                groupMinSelectionId: 1,
                                kickOffAngle: rndAngle70to110,
                                goaliePos: 0.5,
                                targets: [
                                    new CornerTargetSettings(eCornerTargetPos.topLeft),
                                    new MovingTargetSettings(eTargetType.round, 0, [
                                        new MovingTargetPathSectionSettings(0, 1, 1, -1, pc.SineIn, MovingTargetPathSectionSettings.FLAG_ANIM_POINT_A | MovingTargetPathSectionSettings.FLAG_ANIM_POINT_B),
                                        new MovingTargetPathSectionSettings(1, 1, -1, -1, pc.SineOut, MovingTargetPathSectionSettings.FLAG_ANIM_POINT_A | MovingTargetPathSectionSettings.FLAG_ANIM_POINT_B),
                                    ], 0.75, 2, 0, showAnims[0], hideAnims[0]),
                                    new StaticTargetSettings(eTargetType.fireRing, 1, 0.5, 0.5, TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_X | TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_Y),
                                ],
                                defDefenderSettingsId: 1,
                            },
                            // Terc jede zjednoho okraje k druhemu v dolni casti brany, pak zmizi a udela to stejne u horniho okraje, akorat z druhe strany
                            {
                                uid: 36,
                                groupMinSelectionId: 2,
                                kickOffAngle: rndAngle70to110,
                                goaliePos: 0.5,
                                targets: [
                                    new CornerTargetSettings(eCornerTargetPos.topLeft),
                                    new MovingTargetSettings(eTargetType.round, 0, [
                                        new MovingTargetPathSectionSettings(0, 0.05, 1, 0, pc.SineIn, MovingTargetPathSectionSettings.FLAG_ANIM_POINT_A | MovingTargetPathSectionSettings.FLAG_ANIM_POINT_B),
                                        new MovingTargetPathSectionSettings(1, 0.95, -1, 0, pc.SineOut, MovingTargetPathSectionSettings.FLAG_ANIM_POINT_A | MovingTargetPathSectionSettings.FLAG_ANIM_POINT_B),
                                    ], 1, 1.5, 0, showAnims[0], hideAnims[0]),
                                    new StaticTargetSettings(eTargetType.fireRing, 1, new SelNumVal([0.25, 0.75]), new SelNumVal([0.05, 0.95]), TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_X | TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_Y),
                                ],
                                defDefenderSettingsId: 1,
                            },
                            // Terc se pohybuje od okraje k druhemu a postupne zrychluje. U druheho okraje zmizi a zacina znovu u prvniho
                            {
                                uid: 37,
                                groupMinSelectionId: 1,
                                kickOffAngle: rndAngle70to110,
                                goaliePos: 0.5,
                                targets: [
                                    new CornerTargetSettings(eCornerTargetPos.right),
                                    new MovingTargetSettings(eTargetType.round, 0, [new MovingTargetPathSectionSettings(0, rndY0to1, 1, 0, pc.SineIn, MovingTargetPathSectionSettings.FLAG_ANIM_POINT_A | MovingTargetPathSectionSettings.FLAG_ANIM_POINT_B)], 0.75, 2, 0, showAnims[0], hideAnims[0]),
                                    new StaticTargetSettings(eTargetType.fireRing, 1, 0.3, 0, TargetSettingsBase.FLAG_USE_PREV_TARGET_SIZE_FOR_X | TargetSettingsBase.FLAG_USE_PREV_TARGET_Y),
                                ],
                                defDefenderSettingsId: 0
                            }
                        ],
                        selectionMode: eSelectionMode.random
                    }
                ]
            }
        ];

        this._sceneSetupSelector = new SceneSetupSelector();
        this._roundSetup = new RoundSetup();

        // Efekt zablesku pro prechod do dalsiho kola hry
        this._flashSettings = [{
            color: new pc.Color(0, 0, 0),
            alpha: 1,
            easeIn: pc.CubicOut,
            lenIn: 250,
            easeOut: pc.CubicOut,
            lenOut: 250,
            onCompleteIn: (): void => {
                Gameplay.instance.prepareRound();
            },
            onCompleteOut: (): void => {
                this._state = eRoundMngState.playRound;
                Gameplay.instance.events.fire(RoundManager.EVENT_ROUND_READY);
            }
        },
        {
            color: new pc.Color(0, 0, 0),
            alpha: 1,
            easeIn: pc.Linear,
            lenIn: 0,
            easeOut: pc.CubicOut,
            lenOut: 500,
            onCompleteOut: (): void => {
                this._state = eRoundMngState.playRound;
                Gameplay.instance.events.fire(RoundManager.EVENT_ROUND_READY);
            }
        }];
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        this._roundId.value = -1;
        this._startNewRound = true;
        this._sceneSetupSelectorBlockId = 0;
        this._sceneSetupSelector.init(this._sceneSetups[0]);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    prepareRound(): void {
        this._roundStartTime = Math.round(TIMER.time);

        if (this._startNewRound) {
            this._startNewRound = false;
            this._roundId.value++;
            this._roundAttemptId.setToZero();

            // Vybrat data nastaveni pro nove kolo
            let setup = this._sceneSetupSelector.getSceneSetup();

            if (this._sceneSetupSelector.remSceneCnt == 0) {
                // Postup do dalsiho bloku skupin s nastavenim scen
                this._sceneSetupSelector.init(this._sceneSetups[++this._sceneSetupSelectorBlockId]);
            }

            // Inicializovat nastaveni
            this._roundSetup.init(setup, setup.disableFlipSide == undefined || !setup.disableFlipSide ? rnd1.randomBool() : false);

            if (this._roundId.value == 0 && this._roundAttemptId.value == 0) {
                this._state = eRoundMngState.transToNextRound;
                HUD.instance.flashFx.startFx(this._flashSettings[1]);
            }
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    endRound(res: eKickOffResult, remLifeCnt: number): void {
        if (this._state == eRoundMngState.playRound) {
            this._state = eRoundMngState.roundEnded;

            // Spustit prodlevu do zahajeni noveho kola
            this._roundEndTime = TIMER.time + (remLifeCnt != 0 ? 2000 : 1000);

            if (res != eKickOffResult.target && res != eKickOffResult.goal) {
                // Navysit pocet pokusu
                this._roundAttemptId.value++;
            }
            else {
                // Prejit do dalsiho kola
                this._startNewRound = true;
            }
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): void {
        if (this._roundEndTime >= 0 && this._roundEndTime <= TIMER.time) {
            this._roundEndTime = -1;

            if (Gameplay.instance.lifeCnt != 0) {
                // Zahajit nove kolo
                this._state = eRoundMngState.transToNextRound;
                HUD.instance.flashFx.startFx(this._flashSettings[0]);
            }
            else {
                // Ukoncit hru
                Gameplay.instance.gameOver(eGameEndReason.outOfLives);
            }
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    applyExtraLife(): void {
        // Zahajit nove kolo
        this._state = eRoundMngState.transToNextRound;
        HUD.instance.flashFx.startFx(this._flashSettings[0]);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handleGameEnd(): void {
        this._roundEndTime = -1;
    }
}